export enum VideoSource {
  ORA = 'ORA',
  OSMOSIS = 'OSMOSIS',
  ANATOMY = 'ANATOMY',
  CRASH_COURSE = 'CRASH_COURSE',
  YOUTUBE = 'YOUTUBE',
}

export enum AnatomySection {
  THORACIC = 'THORACIC',
  HEART = 'HEART',
  LUNGS = 'LUNGS',
  MEDIASTINUM = 'MEDIASTINUM',
}

export type LiveQuestionOption = {
  id: string;
  label: string;
  /** Why this option is right or wrong (course video checks); HTML. */
  rationale?: string;
};

export type LiveQuestion = {
  id: string;
  enterTime: number;
  autoPause?: number;
  question: string;
  options: LiveQuestionOption[];
  correctOptionId: string;
  isPreQuestion?: boolean;
  isPostQuestion?: boolean;
  revealAnswerTime?: number;
  ord?: number;
};

// Payload shape for the live questions sync endpoint; questions without an id
// are created, existing ids are updated, and omitted ids are deleted.
export type LiveQuestionInput = Omit<LiveQuestion, 'id' | 'ord'> & {
  id?: string;
};

// A user's saved answer to a live question (current round), used to re-hydrate
// the player.
export type LiveQuestionResponse = {
  liveQuestionId: string;
  selectedOptionId: string;
  isCorrect: boolean;
};

// Per-(user, video) live-question answer state, present only on single-video
// and study-session payloads (not list endpoints).
export type LiveQuestionUserState = {
  currentRound: number;
  responses: LiveQuestionResponse[];
};

// Legacy shape still present on VideoVersion responses; live questions now
// live on Video.liveQuestions.
export type TimelineJson = {
  liveQuestions?: Omit<LiveQuestion, 'id'>[];
};

export interface VideoVersion {
  id: string;
  ord?: number;
  // Preferred CDN-delivered URL for playback
  cdnUrl?: string;
  mp4Url?: string;
  webmUrl?: string;
  youtubeURL?: string;
  vttUrl?: string;
  withCam: boolean;
  durationSeconds?: number;
  timelineJson?: TimelineJson;
}

export enum WatchState {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

interface VideoTopic {
  id: string;
  name: string;
  path: string;
  icon: string | null;
  articleId: string | null;
}

export interface VideoLibraryItem {
  id: string;
  title?: string;
  thumbnailUrl: string;
  source: VideoSource;
  ord?: number;
  watchState?: WatchState;
  anatomySection?: AnatomySection;
  viewCount?: number;
  videoTabVisibility: boolean;
  durationSeconds?: number | null;
  isBookmarked?: boolean;
  progressPercent?: number;
  citationAuthor?: string | null;
  sourceLabel?: string;
  availability?: string;
}

export interface Video {
  id: string;
  title?: string;
  description?: string;
  caption: string;
  mp4Url: string;
  webmUrl: string;
  thumbnailUrl: string;
  topics: VideoTopic[];
  citationUrl: string;
  citationAuthor: string;
  citationLicense: string;
  source: VideoSource;
  versions: VideoVersion[];
  liveQuestions?: LiveQuestion[];
  liveQuestionState?: LiveQuestionUserState;
  createdAt: Date;
  updatedAt: Date;
  ord?: number;
  watchState?: WatchState;
  anatomySection?: AnatomySection;
  viewCount?: number;
  videoTabVisibility: boolean;
  catalogAvailability?: 'AVAILABLE' | 'UNAVAILABLE' | 'UNKNOWN';
  availabilityReason?: string | null;
}
